import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px, SWReactCountryIcons } from "@similarweb/icons";
import { combineConfigs, DonutChart, noMarkerConfig } from "@similarweb/pro-logic-components";
import { colorsPalettes, colorsSets } from "@similarweb/styles";
import { Card, Tooltip, TitlePlaceholderLoader, ListItemPlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import GAVerifiedContainer from "app/components/React/GAVerifiedIcon/GAVerifiedContainer";
import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { useWindowSize } from "app/custom-hooks/useWIndowSize";
import type { IWebsiteAnalysisTrafficRequestParams } from "app/pages/website-analysis/TrafficAndEngagement/types";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";

import { tooltipConfig } from "./chartTooltip";
import { Legend } from "./Legend";
import { useDeviceDistributionData } from "./useDeviceDistributionData";

const DONUT_INNER_SIZE = "55%";
const DONUT_SIZE_SMALL = 84;
const DONUT_SIZE_LARGE = 100;
const DONUT_SMALL_SCREEN_BREAKPOINT = 1400;

interface IDeviceDistributionWidgetProps {
    params: {
        country: number;
        from: string;
        to: string;
        key: string;
        webSource: string;
        isWindow: boolean;
        isWWW: boolean;
    };
    dateRangeText?: string;
    countryText?: string;
    countryCode?: number;
    showGAApprovedData: boolean;
    isGAPrivate: boolean;
    apiController?: string;
    showAddToDashboard?: boolean;
}

export const DeviceDistributionWidget = ({
    params,
    dateRangeText,
    countryText,
    countryCode,
    showGAApprovedData,
    isGAPrivate,
    apiController,
    showAddToDashboard = false,
}: IDeviceDistributionWidgetProps) => {
    const { translate } = useTranslate();
    const { durationTriggerProps, countryTriggerProps } = useSelectorTrigger("device-distribution-widget");
    const { width: windowWidth } = useWindowSize();
    const isSmallScreen = (windowWidth ?? DONUT_SMALL_SCREEN_BREAKPOINT) < DONUT_SMALL_SCREEN_BREAKPOINT;

    const donutSize = isSmallScreen ? DONUT_SIZE_SMALL : DONUT_SIZE_LARGE;

    const apiParams: IWebsiteAnalysisTrafficRequestParams = {
        country: params.country.toString(),
        from: params.from,
        to: params.to,
        keys: params.key,
        webSource: params.webSource,
        isWindow: params.isWindow,
        includeSubDomains: !params.isWWW,
        timeGranularity: "Monthly",
        ShouldGetVerifiedData: showGAApprovedData,
    };

    const { data, isLoading, isError } = useDeviceDistributionData(apiParams, apiController);

    const chartConfig = useMemo(
        () =>
            combineConfigs({}, [
                noMarkerConfig,
                {
                    chart: {
                        height: "100%",
                        width: donutSize,
                        spacing: [0, 0, 0, 0],
                    },
                    tooltip: tooltipConfig,
                    plotOptions: {
                        pie: {
                            size: donutSize,
                        },
                    },
                },
            ]),
        [donutSize],
    );

    const isEmpty = useMemo(() => {
        const deviceData = data?.Data?.[params.key];
        if (!deviceData) {
            return true;
        }

        const { Desktop: desktop = 0, "Mobile Web": mobile = 0 } = deviceData;
        return desktop <= 0 && mobile <= 0;
    }, [data, params.key]);

    const chartData = useMemo(() => {
        const deviceData = data?.Data?.[params.key];
        if (!deviceData) {
            return [];
        }

        const { Desktop: desktop = 0, "Mobile Web": mobile = 0 } = deviceData;
        const total = desktop + mobile;

        return [
            {
                data: [
                    {
                        id: "desktop",
                        color: colorsSets.b1["blue 400"],
                        name: "Desktop",
                        visible: true,
                        y: (desktop / total) * 100,
                    },
                    {
                        id: "mobile-web",
                        color: colorsSets.b1["sky 300"],
                        name: "Mobile Web",
                        visible: true,
                        y: (mobile / total) * 100,
                    },
                ],
            },
        ];
    }, [data, params.key]);

    if (isLoading) {
        return (
            <Card data-automation="device-distribution-widget">
                <Flex
                    flexDirection="column"
                    padding="16px 24px"
                    gap="16px"
                >
                    <TitlePlaceholderLoader />
                    <ListItemPlaceholderLoader />
                    <ListItemPlaceholderLoader />
                </Flex>
            </Card>
        );
    }

    const showNoData = isError || isEmpty;

    return (
        <Card data-automation="device-distribution-widget">
            {showNoData ? (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    flex="1"
                    height="100%"
                >
                    <NoDataLandscape
                        title="global.nodata.notavilable"
                        subtitle={null}
                    />
                </Flex>
            ) : (
                <>
                    <Card.Header withDivider>
                        <Card.Title>
                            <Flex
                                gap="8px"
                                alignItems="center"
                            >
                                <Text
                                    fontVariant="h5_2"
                                    data-automation="device-distribution-widget-title"
                                >
                                    {translate("wa.ao.trafficshare")}
                                </Text>
                                <Flex alignItems="center">
                                    {showGAApprovedData ? (
                                        <span data-automation="device-distribution-widget-title-ga-verified">
                                            <GAVerifiedContainer
                                                size="SMALL"
                                                isActive={true}
                                                isPrivate={isGAPrivate}
                                                tooltipAvailable={true}
                                                metric="EngagementDevices"
                                                tooltipIsActive={false}
                                                top={4}
                                            />
                                        </span>
                                    ) : null}
                                    <span data-automation="device-distribution-widget-title-tooltip">
                                        <Tooltip content={translate("wa.ao.trafficshare.tooltip")}>
                                            <Info24px
                                                size={24}
                                                color={colorsPalettes.carbon[200]}
                                                data-automation="device-distribution-widget-title-info-icon"
                                            />
                                        </Tooltip>
                                    </span>
                                </Flex>
                            </Flex>
                            <Card.Query>
                                <Card.QueryItem
                                    iconName="daily-ranking"
                                    data-automation="device-distribution-widget-date-range"
                                    {...durationTriggerProps}
                                >
                                    {dateRangeText}
                                </Card.QueryItem>
                                <Flex
                                    gap="8px"
                                    alignItems="center"
                                    data-automation="device-distribution-widget-country"
                                    {...countryTriggerProps}
                                >
                                    <SWReactCountryIcons
                                        countryCode={countryCode}
                                        size="xs"
                                    />
                                    <Text
                                        fontVariant="chip1"
                                        color={colorsPalettes.carbon[400]}
                                    >
                                        {countryText}
                                    </Text>
                                </Flex>
                            </Card.Query>
                        </Card.Title>
                        {showAddToDashboard ? (
                            <AddToDashboard
                                webSource={params.webSource as IWidgetModelTypesWebSource}
                                type="PieChart"
                                metric="EngagementDesktopVsMobileVisits"
                                modelType="fromWebsite"
                                filters={{
                                    ShouldGetVerifiedData: showGAApprovedData,
                                }}
                            />
                        ) : null}
                    </Card.Header>
                    <Flex
                        width="100%"
                        height="100%"
                        flex="1"
                        alignItems="center"
                        gap={isSmallScreen ? "16px" : "24px"}
                        padding={isSmallScreen ? "16px" : "24px"}
                        minWidth="0"
                    >
                        <Flex flexShrink={0}>
                            <DonutChart
                                data={chartData}
                                config={chartConfig}
                                shouldPresentLegend={false}
                                innerSize={DONUT_INNER_SIZE}
                            />
                        </Flex>
                        <Flex
                            minWidth="0"
                            flex="1"
                            overflow="hidden"
                        >
                            <Legend chartData={chartData[0].data} />
                        </Flex>
                    </Flex>
                </>
            )}
        </Card>
    );
};
